import { ChevronDown } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { DocBlocks } from "@/components/docs/DocBlocks";
import { DocPager } from "@/components/docs/DocPager";
import { DocRelated } from "@/components/docs/DocRelated";
import { DocsShell } from "@/components/docs/DocsShell";
import { DocToc } from "@/components/docs/DocToc";
import { docOutline } from "@/components/docs/headings";
import { JsonLd } from "@/components/docs/JsonLd";
import { docSectionFor, getDocPage } from "@/content/docs/index";
import { docPageJsonLd } from "@/lib/docsJsonLd";
import { NotFound } from "@/routes/NotFound";

export function DocsArticle() {
  const { slug = "index" } = useParams();
  const page = getDocPage(slug);

  if (!page || slug === "index") {
    return <NotFound />;
  }

  const section = docSectionFor(page.slug);
  const hasToc = docOutline(page.blocks).filter((entry) => entry.level === 2).length >= 2;
  const toc = hasToc ? <DocToc blocks={page.blocks} /> : undefined;

  return (
    <DocsShell aside={toc}>
      <JsonLd data={docPageJsonLd(page)} />

      <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
        <Link to="/docs" className="hover:text-foreground">
          Docs
        </Link>
        {section ? (
          <>
            <span className="mx-2" aria-hidden>
              /
            </span>
            <span>{section.label}</span>
          </>
        ) : null}
      </nav>

      <header className="mt-4">
        <h1 className="text-balance font-sans text-3xl font-semibold tracking-tight text-foreground sm:text-4xl sm:leading-[1.12]">
          {page.title}
        </h1>
      </header>

      <article className="mt-8">
        <DocBlocks blocks={page.blocks.slice(0, 1)} />
        {toc ? (
          <details className="group mb-8 rounded-card border border-border bg-muted/30 xl:hidden">
            <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-medium text-foreground">
              On this page
              <ChevronDown
                className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180"
                aria-hidden
              />
            </summary>
            <div className="border-t border-border px-4 py-4 [&>nav>p]:hidden [&>nav>ul]:mt-0">
              {toc}
            </div>
          </details>
        ) : null}
        <DocBlocks blocks={page.blocks.slice(1)} />
      </article>

      <DocRelated slugs={page.related} current={page.slug} />

      <p className="mt-10 text-sm text-muted-foreground">
        Last updated <time dateTime={page.updated}>{formatUpdated(page.updated)}</time>.
      </p>

      <DocPager slug={page.slug} />
    </DocsShell>
  );
}

function formatUpdated(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}
